import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { ColumnType } from 'antd/es/table';

import { sortByAmount, sortByTime } from 'utils/antd/sorters';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';

import {
  PAYOUT_STATUSES,
  PAYOUTS_UNIQUE_REFERENCE
} from '@pages/Payouts/constants';
import type { BatchDraftsItem, PayoutRequestItem } from 'api';

import type { FindAccountByAccountNumber } from 'hooks/context/useVirtualAccounts';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { TransactionIconMinus } from '@atoms/Icon';
import { PaperClipIcon } from '@atoms/Icon/PaperClipIcon';
import { StatusTag } from '@atoms/StatusTag';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

export const getColumns = (
  findAccountByAccountNumber: FindAccountByAccountNumber
): ColumnType<PayoutRequestItem>[] => {
  return [
    {
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: () => (
        <div className="flex justify-center">
          <TransactionIconMinus width={32} height={32} />
        </div>
      )
    },
    {
      title: <ColumnTitle title="Date" />,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => <FormattedDateTime value={createdAt} />,
      sorter: (a, b) => sortByTime(a.created_at, b.created_at),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="ID/Reference" />,
      dataIndex: 'unique_reference',
      width: 200,
      key: 'unique_reference',
      render: (uniqueReference: string, record: PayoutRequestItem) => (
        <div>
          <p className="text-sm">{record.end_to_end_id?.toLocaleUpperCase()}</p>
          <div className="font-semibold" data-testid={PAYOUTS_UNIQUE_REFERENCE}>
            {uniqueReference.toLocaleUpperCase()}
          </div>
        </div>
      ),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="Account" />,
      dataIndex: 'debtor_account',
      key: 'debtor_account',
      align: 'right',
      render: (debtorAccountNumber?: string) => {
        const account = findAccountByAccountNumber(debtorAccountNumber!);
        return (
          <div>
            <p className="text-sm">{debtorAccountNumber}</p>
            <div className="font-semibold">{account?.name}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Amount" />,
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      sorter: (a, b) => sortByAmount(a.amount, b.amount),
      render: (amount: string, record: PayoutRequestItem) => {
        const formattedAmount = formatDecimalAmount(Number.parseFloat(amount));
        return (
          <div>
            <p className="text-sm">{record.currency}</p>
            <div className="font-semibold">{formattedAmount}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'state',
      key: 'state',
      align: 'right',
      className: '!pr-4',
      render: (status: string) => {
        return <StatusTag status={status} statuses={PAYOUT_STATUSES} />;
      }
    }
  ];
};

export const getColumnsDraft = (
  findAccountByAccountNumber: FindAccountByAccountNumber
): ColumnType<BatchDraftsItem>[] => {
  return [
    {
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: () => (
        <div className="flex justify-center">
          <TransactionIconMinus width={32} height={32} />
        </div>
      )
    },
    {
      title: <ColumnTitle title="Date" />,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => <FormattedDateTime value={createdAt} />,
      sorter: (a, b) => sortByTime(a.created_at, b.created_at),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="ID/Reference" />,
      dataIndex: 'end_to_end_id',
      width: 200,
      key: 'end_to_end_id',
      render: (endToEndId: string) => (
        <p className="text-sm">{endToEndId.toLocaleUpperCase()}</p>
      ),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="Account" />,
      dataIndex: 'debtor_account',
      key: 'debtor_account',
      align: 'right',
      render: (debtorAccountNumber?: string) => {
        const account = findAccountByAccountNumber(debtorAccountNumber!);
        return (
          <div>
            <p className="text-sm">{debtorAccountNumber}</p>
            <div className="font-semibold">{account?.name}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Amount" />,
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      sorter: (a, b) => sortByAmount(a.amount, b.amount),
      render: (amount: string, record: BatchDraftsItem) => {
        const formattedAmount = formatDecimalAmount(Number.parseFloat(amount));
        return (
          <div>
            <p className="text-sm">{record.currency}</p>
            <div className="font-semibold">{formattedAmount}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'state',
      key: 'state',
      align: 'right',
      className: '!pr-4',
      render: (status: string) => {
        return <StatusTag status={status} statuses={PAYOUT_STATUSES} />;
      }
    },
    {
      dataIndex: 'attachments',
      key: 'paperClipIcon',
      align: 'right',
      width: 30,
      render: (_: unknown, record: BatchDraftsItem) => {
        if (record.attachments_count > 0) {
          return <PaperClipIcon className="h-9 stroke-content-subtle pt-3" />;
        }
        return null;
      }
    },
    {
      dataIndex: 'icon',
      key: 'arrowIcon',
      align: 'right',
      width: 50,
      render: () => (
        <ChevronRightIcon className="h-9 stroke-content-subtle pt-3" />
      )
    }
  ];
};
